import usePrivateAxios from "@/common/api/usePrivateAxios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { MissionSchema } from "../schemas/mission.schema";
import { frontendRewardTypeToBackend } from "../utils/missionMapper";
import { MissionRewardType } from "../types/mission.type";
import { MissionFormTransformer } from "../services/missionFormTransformer";

interface UseUpdateMissionProps {
  onSuccess?: (response: AxiosResponse) => void
  onError?: (error: AxiosError) => void
}

type UpdateVariables = {
  id: string
  form: MissionSchema
}

const isTemporaryId = (value?: string) => !value || value.startsWith('mission-field')

export const useUpdateMission = ({ onSuccess, onError }: UseUpdateMissionProps = {}) => {
  const privateAxios = usePrivateAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['updateMission'],
    mutationFn: async ({ id, form }: UpdateVariables) => {
      const missionSnapshot = queryClient.getQueryData(['mission', id]) as any

      const activeConditionIds = new Set(
        form.conditions.map((c) => c.id).filter((condId) => !isTemporaryId(condId))
      )
      
      const removedRequirements = (missionSnapshot?.conditions || [])
        .filter((c: any) => c.id && !activeConditionIds.has(c.id))
        .map((c: any) => ({ id: c.id, _destroy: true }))

      const activeRewardIds = new Set(
        form.rewards.map((r) => r.id).filter((rewId) => !isTemporaryId(rewId))
      )
      
      const removedRewards = (missionSnapshot?.rewards || [])
        .filter((r: any) => {
          const relationId = r.mission_reward_id || r.pivot_id || r.id
          return relationId && !activeRewardIds.has(relationId)
        })
        .map((r: any) => {
          const relationId = r.mission_reward_id || r.pivot_id || r.id
          return { id: relationId, _destroy: true }
        })

      const maxPerUser = form.allow_multiple_times ? form.max_times : 1
      const payload = {
        mission_type: form.mission_type,
        display_name: form.name,
        description: form.description,
        status: form.status,
        minimum_loyalty: form.minimum_loyalty,
        available_from: MissionFormTransformer.parseDateField(form.start_date),
        available_until: MissionFormTransformer.parseDateField(form.end_date),
        maximum_completions_per_user: maxPerUser || null,
        establishment_company_branch_id:
          form.mission_type === 'establishment' ? form.establishment?.id : null,
        requirements_attributes: [
          ...form.conditions.map((cond) => {
            const conditionProperties = {
              ...(cond.properties || {}),
              ...(cond.type_label ? { label: cond.type_label } : {}),
              ...(cond.condition_group_label ? { condition_group_label: cond.condition_group_label } : {}),
              ...(cond.value_label ? { value_label: cond.value_label } : {}),
            }

            return {
              id: isTemporaryId(cond.id) ? undefined : cond.id,
              provider_klass: cond.provider_klass || cond.type_label,
              quantity: MissionFormTransformer.resolveRequirementQuantity(cond),
              condition_group_label: cond.condition_group_label || 'general',
              properties: conditionProperties,
            }
          }),
          ...removedRequirements
        ],
        rewards_attributes: [
          ...form.rewards.map((rew) => {
            const rewardProperties: Record<string, unknown> = {}
            if (rew.reward_label) rewardProperties.label = rew.reward_label
            if (rew.product_name) rewardProperties.product_name = rew.product_name
            const frontendRewardType = rew.reward_type as MissionRewardType
            const backendRewardType = frontendRewardTypeToBackend(frontendRewardType)
            const isPointsReward = frontendRewardType === "points"
            const normalizedRewardId = !isPointsReward
              ? rew.product_id || rew.reward_id
              : undefined
            return {
              id: isTemporaryId(rew.id) ? undefined : rew.id,
              reward_type: isPointsReward ? null : backendRewardType,
              reward_id:
                isPointsReward
                  ? null
                  : normalizedRewardId
                  ? String(normalizedRewardId)
                  : undefined,
              quantity: Number(rew.quantity ?? 1),
              properties: rewardProperties,
            }
          }),
          ...removedRewards
        ],
      }

      return privateAxios.put(`/tmais/missions/${id}`, { mission: payload })
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['mission', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['missions'] })
      onSuccess?.(response)
    },
    onError,
  })
}